import { useState, useEffect, useCallback } from "react";
import { LoadPanel } from "devextreme-react";
import Splitter, { Item as SplitterItem } from "devextreme-react/splitter";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown, faChevronUp } from "@fortawesome/free-solid-svg-icons";
import { CustomDataGrid } from "../CustomDataGrid";
import { ContactInfoPane } from "./ContactInfoPane";
import { Contact, MOCK_CONTACTS, parseVendorCodes } from "./contact-mock";
import "../messages/TicketPage.css";

// Kullanicilar (contacts) — OrganizationsPage/TicketPage duzeni:
// ust panelde CustomDataGrid liste, alt panelde cok-sekmeli (MDI) kullanici detayi.
// Satira tiklayinca altta secili kullanici ContactInfoPane olarak acilir.
type ContactTab = { ref: string; contactRef: string; title: string };
const createTabId = () => `ctab-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

export default function ContactsPage() {
    const { t } = useTranslation();

    const [contacts, setContacts] = useState<Contact[]>([]);
    const [loading, setLoading] = useState(true);
    const [gridKey, setGridKey] = useState(0);

    // MDI sekmeler
    const [tabs, setTabs] = useState<ContactTab[]>([]);
    const [activeTabId, setActiveTabId] = useState<string | null>(null);
    const [topCollapsed, setTopCollapsed] = useState(false);
    const [bottomCollapsed, setBottomCollapsed] = useState(false);

    const activeTab = tabs.find(tb => tb.ref === activeTabId) || null;

    const toggleBottom = useCallback(() => {
        setBottomCollapsed(prev => !prev);
        setTopCollapsed(false);
        setTimeout(() => setGridKey(k => k + 1), 250);
    }, []);

    const toggleTop = useCallback(() => {
        setTopCollapsed(prev => !prev);
        setBottomCollapsed(false);
        setTimeout(() => setGridKey(k => k + 1), 250);
    }, []);

    // ── Load contacts ─────────────────────────────────────────────────────────
    // Backend /api/contacts servisi gelince burada getContacts() cagrilir; simdilik mock.
    const load = useCallback(() => {
        setLoading(true);
        setContacts(MOCK_CONTACTS);
        setLoading(false);
    }, []);

    useEffect(() => { load(); }, [load]);

    // ── Tab yonetimi ──────────────────────────────────────────────────────────
    const openContactInTab = useCallback((contact: Contact) => {
        setTabs(prev => {
            const existing = prev.find(tb => tb.contactRef === String(contact.ref));
            if (existing) { setActiveTabId(existing.ref); return prev; }
            const tabId = createTabId();
            setActiveTabId(tabId);
            return [...prev, { ref: tabId, contactRef: String(contact.ref), title: contact.userName || `#${contact.ref}` }];
        });
    }, []);

    const closeTab = useCallback((tabId: string) => {
        setTabs(prev => {
            if (prev.length <= 1) { setActiveTabId(null); return []; }
            const idx = prev.findIndex(tb => tb.ref === tabId);
            const next = prev.filter(tb => tb.ref !== tabId);
            setActiveTabId(cur => cur === tabId ? (next[Math.max(0, idx - 1)] || next[0])?.ref || null : cur);
            return next;
        });
    }, []);

    // Satir secimi: aktif sekme varsa onu degistir, yoksa yeni sekme ac (OrganizationsPage davranisi).
    const selectContact = useCallback((contact: Contact) => {
        if (!activeTabId || tabs.length === 0) { openContactInTab(contact); return; }
        setTabs(prev => prev.map(tb => tb.ref === activeTabId
            ? { ...tb, contactRef: String(contact.ref), title: contact.userName || `#${contact.ref}` }
            : tb));
    }, [activeTabId, tabs.length, openContactInTab]);

    const handleRowClick = (data: Contact) => selectContact(data);
    const handleFocusedRowChanged = useCallback((e: any) => {
        if (e?.row?.data) selectContact(e.row.data);
    }, [selectContact]);

    const columns = [
        { dataField: "userName", caption: t("userName"), width: 200 },
        { dataField: "email", caption: t("email"), width: 240 },
        { dataField: "firstName", caption: t("firstName"), width: 160 },
        { dataField: "lastName", caption: t("lastName"), width: 160 },
        {
            dataField: "vendors",
            caption: t("vendors"),
            width: 320,
            allowSorting: false,
            cellRender: (d: any) => {
                const codes = parseVendorCodes(d.value);
                if (codes.length === 0) return <span style={{ color: "var(--subtitle-text-color)" }}>—</span>;
                const shown = codes.slice(0, 3);
                const rest = codes.length - shown.length;
                return (
                    <div className="flex flex-wrap items-center gap-1" title={codes.join(", ")}>
                        {shown.map(code => (
                            <span key={code} className="font-mono text-xs" style={{ background: "var(--base-bg-darken-4)", color: "var(--base-text-color)", padding: "2px 7px", borderRadius: 6 }}>
                                {code}
                            </span>
                        ))}
                        {rest > 0 && (
                            <span className="text-xs" style={{ color: "var(--subtitle-text-color)" }}>+{rest}</span>
                        )}
                    </div>
                );
            },
        },
        {
            dataField: "isActive",
            caption: t("status"),
            width: 110,
            alignment: "center" as const,
            cellRender: (d: any) => {
                const active = d.value !== false;
                const color = active ? "#10B981" : "#9CA3AF";
                return (
                    <span style={{ background: `${color}1f`, color, fontWeight: 600, fontSize: 12, padding: "2px 10px", borderRadius: 999 }}>
                        {active ? t("active") : t("passive")}
                    </span>
                );
            },
        },
        { dataField: "", caption: "", width: undefined, allowSorting: false, allowFiltering: false },
    ];

    return (
        <div className="ticket-page-root flex flex-col h-full w-full" style={{ paddingBottom: 30 }}>
            <LoadPanel visible={loading} />

            <div className="ticket-splitter-wrap flex-1 min-h-0">
                <Splitter orientation="vertical">
                    {/* Üst Pane - Kullanici listesi */}
                    <SplitterItem size="40%" minSize="200px" collapsed={topCollapsed}>
                        <CustomDataGrid
                            key={`contacts-grid-${gridKey}`}
                            className="custom-grid compact-grid"
                            data={contacts}
                            columns={columns}
                            keyExpr="ref"
                            height="100%"
                            t={t}
                            noDataText={t("noDataText")}
                            onRefresh={load}
                            onRowClick={handleRowClick}
                            onFocusedRowChanged={handleFocusedRowChanged}
                            focusedRowKey={activeTab?.contactRef ?? undefined}
                            columnChooserEnabled
                            filterRowVisible={false}
                            rowAlternationEnabled={true}
                            allowColumnReordering
                            exportEnabled
                            additionalToolbarItems={[
                                {
                                    location: "after",
                                    render: () => (
                                        <button
                                            type="button"
                                            className="splitter-collapse-btn"
                                            title={t("closeBottomPane")}
                                            onClick={toggleBottom}
                                        >
                                            <FontAwesomeIcon icon={bottomCollapsed ? faChevronUp : faChevronDown} />
                                        </button>
                                    ),
                                },
                            ]}
                        />
                    </SplitterItem>

                    {/* Alt Pane - Sekmeli kullanici detayi */}
                    <SplitterItem size="60%" minSize="300px" collapsed={bottomCollapsed}>
                        <div className="h-full flex flex-col overflow-hidden" style={{ background: "var(--background-color)", padding: "0 12px" }}>
                            {tabs.length > 0 ? (
                                <>
                                    {/* Tab Bar */}
                                    <div className="detail-header-tabs shrink-0">
                                        {tabs.map(tab => (
                                            <div
                                                key={tab.ref}
                                                className={`detail-header-tab ${tab.ref === activeTabId ? "active" : ""}`}
                                                title={tab.title}
                                                onAuxClick={(e) => { if (e.button === 1) { e.preventDefault(); closeTab(tab.ref); } }}
                                            >
                                                <button type="button" className="detail-header-tab-label" onClick={() => setActiveTabId(tab.ref)}>
                                                    <i className="dx-icon-user detail-header-tab-icon" />
                                                    <span className="detail-header-tab-title-text">{tab.title}</span>
                                                </button>
                                                <button
                                                    type="button"
                                                    className="detail-header-tab-close"
                                                    onClick={(e) => { e.stopPropagation(); closeTab(tab.ref); }}
                                                    title={t("closeTab")}
                                                >
                                                    ×
                                                </button>
                                            </div>
                                        ))}
                                        <button
                                            type="button"
                                            className="splitter-collapse-btn"
                                            style={{ marginLeft: "auto" }}
                                            title={t("closeTopPane")}
                                            onClick={toggleTop}
                                        >
                                            <FontAwesomeIcon icon={topCollapsed ? faChevronDown : faChevronUp} />
                                        </button>
                                    </div>

                                    {/* Tab Content */}
                                    <div className="flex-1 min-h-0">
                                        {tabs.map(tab => (
                                            <div key={tab.ref} className="h-full" style={{ display: tab.ref === activeTabId ? "block" : "none" }}>
                                                <ContactInfoPane contactRef={tab.contactRef} />
                                            </div>
                                        ))}
                                    </div>
                                </>
                            ) : (
                                <>
                                    <div className="detail-header-tabs shrink-0">
                                        <button
                                            type="button"
                                            className="splitter-collapse-btn"
                                            style={{ marginLeft: "auto" }}
                                            title={t("closeTopPane")}
                                            onClick={toggleTop}
                                        >
                                            <FontAwesomeIcon icon={topCollapsed ? faChevronDown : faChevronUp} />
                                        </button>
                                    </div>
                                    <div className="flex items-center justify-center flex-1" style={{ color: "var(--subtitle-text-color)" }}>
                                        {t("selectContactForDetail")}
                                    </div>
                                </>
                            )}
                        </div>
                    </SplitterItem>
                </Splitter>
            </div>
        </div>
    );
}
